import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { upsertNotification, setSocketStatus, resetNotifications } from '../redux/notifications/notificationsSlice'

const buildNotificationWsUrl = (token) => {
    try {
        const api = new URL(process.env.REACT_APP_API_BASE_URL || '')
        const proto = api.protocol === 'https:' ? 'wss:' : 'ws:'
        return `${proto}//${api.host}/ws/notifications/?token=${encodeURIComponent(token || '')}`
    } catch {
        return ''
    }
}

const MAX_BACKOFF_MS = 30000

// Keeps a single live notification socket open for the whole app (mounted
// once, alongside the header). Reconnects with backoff on drop, and the
// effect itself tears down + reopens whenever the access token changes
// (login, logout, or a silent refresh from the service.js interceptor).
export default function useNotificationSocket() {
    const dispatch = useDispatch()
    const accessToken = useSelector((state) => state.auth.access_token)
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn)

    const wsRef = useRef(null)
    const retryRef = useRef(0)
    const retryTimeoutRef = useRef(null)
    const stoppedRef = useRef(false)

    useEffect(() => {
        if (!isLoggedIn || !accessToken) {
            dispatch(resetNotifications())
            return undefined
        }

        stoppedRef.current = false

        const connect = () => {
            const url = buildNotificationWsUrl(accessToken)
            if (!url) return

            dispatch(setSocketStatus('connecting'))
            const ws = new WebSocket(url)
            wsRef.current = ws

            ws.onopen = () => {
                retryRef.current = 0
                dispatch(setSocketStatus('open'))
            }

            ws.onmessage = (event) => {
                try {
                    const payload = JSON.parse(event.data)
                    if (payload.type === 'notification' && payload.notification) {
                        dispatch(upsertNotification(payload.notification))
                    }
                } catch {
                    // ignore malformed frames
                }
            }

            ws.onclose = () => {
                dispatch(setSocketStatus('closed'))
                if (stoppedRef.current) return
                const delay = Math.min(1000 * 2 ** retryRef.current, MAX_BACKOFF_MS)
                retryRef.current += 1
                retryTimeoutRef.current = setTimeout(connect, delay)
            }

            ws.onerror = () => {
                ws.close()
            }
        }

        connect()

        return () => {
            stoppedRef.current = true
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
            wsRef.current?.close()
            wsRef.current = null
        }
    }, [accessToken, isLoggedIn, dispatch])
}
